import {
  SensitivityAnalysisOutput,
  UnitRateModelFeatureKey,
  defaultUnitRateModelFeatureKeys,
  orUndefined,
  schUnitRateModels,
} from '@aloft/toolkit-shared';
import { Flex, Icon, SimpleSelect, Typography, colors } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-direct-chakra, aloft/prefer-sound-ui-button, import/no-extraneous-dependencies
import {
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';

import { useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { PropertyGrid } from '../../../components/PropertyGrid/PropertyGrid.tsx';
import { PropertyGridSensitivityContext } from '../../../components/PropertyGrid/PropertyGridSensitivityContext.tsx';
import { SensitivityRateSection } from '../../../components/PropertyGrid/columnSections/SensitivityRateSection.tsx';
import { SensitivityUnitRateLookupSection } from '../../../components/PropertyGrid/columnSections/SensitivityUnitRateLookupSection.tsx';
import { useGetTkv2UnitRateModelsAPI } from '../../../hooks/api/toolkitComponents.ts';
import { StatsRow } from './StatsRow.tsx';

const featureLabels: Record<UnitRateModelFeatureKey, string> = {
  gla: 'Gross Living Area',
  bedrooms: 'Bedrooms',
  fullBaths: 'Full Baths',
  halfBaths: 'Half Baths',
  garageCarport: 'Garage / Carport',
  actualAge: 'Actual Age',
  siteSize: 'Site Size',
};

export const SensitivityAnalysisModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const orderID = useParams().orderId!;

  const [selectedFeatureKey, setSelectedFeatureKey] = useState<UnitRateModelFeatureKey>('gla');

  const unitRateModelsQuery = useGetTkv2UnitRateModelsAPI(
    { pathParams: { orderID } },
    { staleTime: Infinity },
  );

  const sensitivityAnalysis: SensitivityAnalysisOutput | undefined = orUndefined(
    schUnitRateModels.parse,
  )(unitRateModelsQuery.data?.body)?.sensitivityAnalysis;

  const selectedResult = sensitivityAnalysis?.[selectedFeatureKey];

  const sensitivityContextValue = useMemo(
    () => ({ sensitivityResult: selectedResult, sensitivityFeatureKey: selectedFeatureKey }),
    [selectedResult, selectedFeatureKey],
  );

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent
        bg={colors.surface3}
        onKeyDown={(e) => e.stopPropagation()}
        maxH="90vh"
        overflow="hidden"
      >
        <ModalCloseButton />
        <ModalBody padding="1.5rem" overflow="hidden" display="flex" flexDirection="column">
          <Flex flexDirection="column" gap="1rem" height="100%" overflow="hidden">
            <Flex alignItems="center" gap="0.75rem" flexShrink={0}>
              <Icon icon="chart-mixed" size="xl" />
              <Typography variant="h1">Sensitivity Analysis Model</Typography>
            </Flex>

            <Flex flexShrink={0}>
              <Typography variant="body2" color="textSecondary">
                Toolkit&apos;s sensitivity model is built using an assumption on the most likely
                values for a given variable. Select a variable to see the analysis details:
              </Typography>
            </Flex>

            {sensitivityAnalysis ? (
              <>
                <Flex gap="1.5rem" flexWrap="wrap" flexShrink={0} flexDirection="column">
                  <Flex alignItems="center" gap="0.5rem" width="fit-content">
                    <Typography variant="body2" color="textSecondary">
                      Variable:
                    </Typography>
                    <SimpleSelect
                      size="sm"
                      value={selectedFeatureKey}
                      onChange={(e) =>
                        setSelectedFeatureKey(e.target.value as UnitRateModelFeatureKey)
                      }
                    >
                      {defaultUnitRateModelFeatureKeys.map((key) => (
                        <option key={key} value={key}>
                          {featureLabels[key]}
                        </option>
                      ))}
                    </SimpleSelect>
                  </Flex>

                  {selectedResult && (
                    <Flex justifyContent="center">
                      <StatsRow type="sensitivity" result={selectedResult} />
                    </Flex>
                  )}
                </Flex>

                <Flex flexDirection="column" overflow="auto" flex={1}>
                  <PropertyGridSensitivityContext.Provider value={sensitivityContextValue}>
                    <PropertyGrid
                      gridType="SensitivityGrid"
                      columnSections={[SensitivityUnitRateLookupSection, SensitivityRateSection]}
                      labelColumnHeader={
                        <Flex
                          width="100%"
                          alignItems="center"
                          gap="0.5rem"
                          padding="0.5rem"
                          backgroundColor="surface4"
                        >
                          <Typography variant="label" color="textSecondary">
                            Property
                          </Typography>
                        </Flex>
                      }
                    />
                  </PropertyGridSensitivityContext.Provider>
                </Flex>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Sensitivity analysis data is not available.
              </Typography>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </ChakraModal>
  );
};
